/**
 * 音声キュー (AudioCue) の純関数。BGM トラック (`bgmTrack`) と将来の SE トラック (`seTrack`) で共有する。
 *
 * 正本: docs/features/video-editor-bgm-track-plan.md
 * - §6.1 周回の展開 (`posInSource` / `expandAudioCuePieces`)
 * - §8.2 分割 (`splitAudioCue`)
 * - §7   書き出しのトラック割り当て (`assignAudioTracks`)
 * - §5.3 (a) プロジェクトの移行 (`migrateProjectBgmTrack` / `legacyBgmToCue`)
 *
 * ここはレンダラー (プレビュー = MP4) と書き出し (Premiere / FCPXML) と UI と AI の ops が
 * **同じ式**を使うための唯一の置き場。zod にも React にも依存しない (Node の単体テストで叩ける)。
 * video-editor リポジトリの src/lib/audioCue.ts に同じ内容の写しがある (import パスのみ差異)。
 */

/**
 * 純関数が必要とするキューの形だけを構造的に宣言する。
 * 両リポジトリの `AudioCue` (zod 由来・materialId の必須/任意が異なる) がそのまま代入できるようにする。
 */
export interface AudioCueLike {
  id: string;
  /** 動画全体の絶対秒での開始 */
  startSec: number;
  /** 区間の終わり。省略 / null = 動画の終わりまで (loop === false かつ素材尺が分かるときは素材の実尺まで) */
  endSec?: number | null;
  /** 曲側の入り点。初回の片にしか効かない (周回は常に曲頭 0 から。§6.1) */
  sourceStartSec?: number;
  /** 素材の実尺 (秒)。挿入時のスナップショット */
  sourceDurationSec?: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  /** 省略 = true (BGM の既定)。false = 繰り返さない (SE) */
  loop?: boolean;
  muted?: boolean;
  /**
   * 再生速度 (音程は保つ)。省略 = 1 (等倍)。範囲外は `audioCueSpeed` が AUDIO_CUE_SPEED_MIN〜MAX に寄せる。
   * タイムラインの1秒で素材が `speed` 秒進む (区間・周回の長さはタイムライン秒、入り点は素材秒)。
   */
  speed?: number;
}

/** 周回を展開した 1 片。レンダラーの `<Audio>` 1 つ / 書き出しのクリップ 1 つに対応する。 */
export interface AudioCuePiece {
  /** 動画全体の絶対秒での開始 */
  startSec: number;
  durationSec: number;
  /** 素材のどこから鳴らすか (Remotion の startFrom / Premiere の in 点) */
  sourceStartSec: number;
  /**
   * true = 素材尺が不明なため周回を展開できず、Remotion の `<Audio loop>` に任せる片 (§6.1)。
   * 書き出し側はクリップ長を決められないので、この片が出たら素材尺の補填を求める。
   */
  loopFallback?: true;
}

/** `expandAudioCuePieces` の返り値。正規化で落とした情報も一緒に返す (呼び出し側が dropped に記録する)。 */
export interface ExpandedAudioCue {
  pieces: AudioCuePiece[];
  /** 区間の絶対秒 (外側の `<Sequence>` / 書き出しのクリップ範囲) */
  startSec: number;
  endSec: number;
  normalized: {
    /** 素材尺が不明なので曲側の入り点 (sourceStartSec) を 0 に落とした (§6.1) */
    sourceStartSecDropped?: true;
  };
}

/** 区間の最小の長さ (秒)。§5.2 の不変条件・§8.2 の分割の下限。BE の `min_window_sec` と同じ値。 */
export const MIN_AUDIO_CUE_SEC = 0.5;

/**
 * 効果音 (SE) の区間の最小の長さ (秒)。
 * docs/features/video-editor-capability-gaps-plan.md §4.3-D:
 * BGM 前提の 0.5 秒を SE に当てると「0.2 秒のポップ音を素材尺どおりに置く」が却下されるため、
 * SE だけ下限を下げる。SE は `endSec` 省略 (素材の実尺どおり鳴らす) が既定で、区間長の検査は
 * `endSec` を明示したときだけ効く。BE の audio_cue.MIN_SE_CUE_SEC と同値。
 */
export const MIN_SE_CUE_SEC = 0.05;

/** 効果音 (SE) の既定音量。SE は「そのまま鳴らす」が既定でフェードも掛けない (§4.3-D)。 */
export const DEFAULT_SE_VOLUME = 1;

/**
 * 音声トラックの種別。キューの型は BGM と SE で共通だが、検証規則 (最小尺・既定音量・
 * ループの既定) だけが違うので、規則を持つ関数はこの種別を引数に取る (§4.3-D)。
 */
export type AudioTrackKind = 'bgm' | 'se';

/** SE を実尺なしで置こうとしたときの文言 (3 実装で同じ意味)。 */
export const MISSING_SE_SOURCE_DURATION_MESSAGE =
  '効果音は素材の実尺 (sourceDurationSec) が分からないと置けません';

/** トラック種別ごとの区間の最小の長さ (秒)。BE の `min_audio_cue_sec` と同値。 */
export function minAudioCueSec(kind: AudioTrackKind = 'bgm'): number {
  return kind === 'se' ? MIN_SE_CUE_SEC : MIN_AUDIO_CUE_SEC;
}

/** 音声キューの再生速度の範囲 (シーンの速度メニューの選択肢と同じ0.5〜2倍)。BE の audio_cue と同値。 */
export const AUDIO_CUE_SPEED_MIN = 0.5;
export const AUDIO_CUE_SPEED_MAX = 2;

/** キューの再生速度。省略・非数・0 以下は 1 (等倍)、範囲外は AUDIO_CUE_SPEED_MIN〜MAX に寄せる。 */
export function audioCueSpeed(cue: Pick<AudioCueLike, 'speed'>): number {
  const speed = cue.speed;
  if (typeof speed !== 'number' || !Number.isFinite(speed) || speed <= 0) return 1;
  return Math.min(AUDIO_CUE_SPEED_MAX, Math.max(AUDIO_CUE_SPEED_MIN, speed));
}

/** 秒の比較に使う許容誤差。分割の許容範囲の「ちょうど」を成功させるため (§16 の 3)。 */
const EPS = 1e-6;

/**
 * フェードイン / フェードアウトの秒を区間長に収める (合計が区間長を超えたら比例縮小)。
 * BGM キューの `validateAudioCue` と同じ規則を、ナレーション 1 本ごとの音量 / フェード
 * (§4.3-E) とシーン音声のフェードでも使えるよう切り出したもの。
 * 負値・非数は 0 として扱う。
 */
export function clampFadePair(
  fadeInSec: number | undefined,
  fadeOutSec: number | undefined,
  spanSec: number,
): { fadeInSec: number; fadeOutSec: number } {
  const toSec = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
  let fadeIn = toSec(fadeInSec);
  let fadeOut = toSec(fadeOutSec);
  if (!(spanSec > 0)) return { fadeInSec: 0, fadeOutSec: 0 };
  if (fadeIn + fadeOut > spanSec) {
    const scale = spanSec / (fadeIn + fadeOut);
    fadeIn *= scale;
    fadeOut *= scale;
  }
  return { fadeInSec: fadeIn, fadeOutSec: fadeOut };
}

/**
 * 区間の頭からの経過秒 `tSec` におけるフェードのゲイン (0〜1)。
 * 区間長 (`spanSec`) を超えるフェード指定は `clampFadePair` で比例縮小してから評価する。
 */
export function fadeGainAt(
  tSec: number,
  spanSec: number,
  fadeInSec: number | undefined,
  fadeOutSec: number | undefined,
): number {
  const { fadeInSec: fadeIn, fadeOutSec: fadeOut } = clampFadePair(fadeInSec, fadeOutSec, spanSec);
  let gain = 1;
  if (fadeIn > 0 && tSec < fadeIn) gain = Math.min(gain, Math.max(0, tSec / fadeIn));
  if (fadeOut > 0 && tSec > spanSec - fadeOut) {
    gain = Math.min(gain, Math.max(0, (spanSec - tSec) / fadeOut));
  }
  return Math.min(1, Math.max(0, gain));
}

function isFiniteDuration(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * キュー内の相対秒 u (0 = 区間の頭) で鳴っている素材位置 (§6.1)。
 *
 * ```
 * v = u × speed               // タイムラインの経過を素材の経過へ
 * L0 = D - s
 * pos(u) = s + v              (v <  L0)
 *        = (v - L0) mod D     (v >= L0)   // 2 周目以降は常に曲頭 0 から
 * ```
 * 素材尺 (D) が不明なときと `loop === false` は周回しないので `s + v` を返す。
 */
export function posInSource(cue: AudioCueLike, u: number): number {
  const s = cue.sourceStartSec ?? 0;
  const duration = cue.sourceDurationSec;
  const v = u * audioCueSpeed(cue);
  if (!isFiniteDuration(duration) || cue.loop === false) {
    return s + v;
  }
  const startPoint = s < duration ? s : 0;
  const firstPieceSec = duration - startPoint;
  if (v < firstPieceSec) {
    return startPoint + v;
  }
  return (v - firstPieceSec) % duration;
}

/**
 * キューの区間の終わり (絶対秒)。
 * - `endSec` があればそこまで (動画の合計尺でクランプ)
 * - `loop === false` かつ素材尺が分かるなら `startSec + (D - s) / speed` まで (素材の実尺どおり)
 * - それ以外は動画の終わりまで
 */
export function effectiveEndSec(cue: AudioCueLike, totalSec: number): number {
  if (cue.endSec != null) {
    return Math.min(cue.endSec, totalSec);
  }
  const duration = cue.sourceDurationSec;
  if (cue.loop === false && isFiniteDuration(duration)) {
    const s = cue.sourceStartSec ?? 0;
    const startPoint = s < duration ? s : 0;
    return Math.min(totalSec, cue.startSec + (duration - startPoint) / audioCueSpeed(cue));
  }
  return totalSec;
}

/**
 * 周回を「片」に展開する (§6.1)。レンダラーと書き出しはこの列だけを見る。
 * - 片 0 = 素材の `sourceStartSec` から `D - sourceStartSec` 秒 (タイムラインでは ÷ speed 秒)
 * - 片 k>=1 = 素材の 0 から `D` 秒 (同 ÷ speed。最後の片は区間の終わりで切る)
 * - 片の `startSec` / `durationSec` はタイムライン秒、`sourceStartSec` は素材秒
 * - `loop === false` は片 0 だけ
 * - 素材尺 (D) が不明: `sourceStartSec > 0` は 0 に落として `normalized` に記録し、
 *   `loop !== false` なら `loopFallback` の片 1 つ (Remotion の `<Audio loop>` に委ねる)、
 *   `loop === false` なら繰り返さない片 1 つ
 */
export function expandAudioCuePieces(cue: AudioCueLike, totalSec: number): ExpandedAudioCue {
  const startSec = cue.startSec;
  const endSec = effectiveEndSec(cue, totalSec);
  const span = endSec - startSec;
  const normalized: ExpandedAudioCue['normalized'] = {};
  if (!(span > 0)) {
    return { pieces: [], startSec, endSec, normalized };
  }

  const duration = cue.sourceDurationSec;
  const rawSourceStart = cue.sourceStartSec ?? 0;

  if (!isFiniteDuration(duration)) {
    // 素材尺が不明 (旧キュー)。入り点は使えないので 0 に落とす (§6.1)
    if (rawSourceStart > 0) {
      normalized.sourceStartSecDropped = true;
    }
    const piece: AudioCuePiece = { startSec, durationSec: span, sourceStartSec: 0 };
    if (cue.loop !== false) {
      piece.loopFallback = true;
    }
    return { pieces: [piece], startSec, endSec, normalized };
  }

  // sourceStartSec が素材尺以上のキューは鳴らないので曲頭に落とす (§5.2 は validateProject で弾くが、
  // 純関数としては音を消さないほうを選ぶ)
  let sourceStart = rawSourceStart;
  if (sourceStart >= duration) {
    sourceStart = 0;
    normalized.sourceStartSecDropped = true;
  }

  // 素材の長さをタイムライン秒へ (速度2倍なら半分の尺で1周する)
  const speed = audioCueSpeed(cue);
  const firstPieceSec = (duration - sourceStart) / speed;
  const loopSec = duration / speed;
  const pieces: AudioCuePiece[] = [
    { startSec, durationSec: Math.min(firstPieceSec, span), sourceStartSec: sourceStart },
  ];

  if (cue.loop !== false) {
    let offset = firstPieceSec;
    while (offset < span - EPS) {
      pieces.push({
        startSec: startSec + offset,
        durationSec: Math.min(loopSec, span - offset),
        sourceStartSec: 0,
      });
      offset += loopSec;
    }
  }

  return { pieces, startSec, endSec, normalized };
}

export interface SplitAudioCueOptions {
  /** 動画全体の合計尺。`endSec` 省略のキューの区間の終わりを決めるのに使う */
  totalSec: number;
  /** 後半のキュー ID。呼び出し側が採番して渡すのが基本 (省略時は元 ID + 分割位置) */
  newId?: string;
  /** トラック種別 (既定 'bgm')。分割後の片の最小尺だけが変わる (§4.3-D) */
  kind?: AudioTrackKind;
  /** タイムラインの fps。渡すと「短すぎる」理由の下限を "S:FF" (秒:フレーム) で書く (省略時は秒) */
  fps?: number;
}

export type SplitAudioCueResult<C extends AudioCueLike> =
  | { ok: true; before: C; after: C }
  | { ok: false; reason: string };

/**
 * キューを絶対秒 t で 2 つに分ける (§8.2)。
 *
 * 前半 = `{...c, endSec: t, fadeOutSec: 0}` / 後半 = `{...c, id: 新規, startSec: t,
 * sourceStartSec: pos(t - startSec), fadeInSec: 0}`。つなぎ目の両側はフェード 0 = 音量 100% で
 * 連続するので、分割前後で音が変わらない。
 *
 * 拒否する位置 (reason はユーザー向けの日本語):
 * - 素材尺が不明 (周回の位相が計算できない)
 * - `startSec` / 区間の終わり ちょうど、および区間の外
 * - フェードイン / フェードアウトの途中 (音量が跳ねるため初期版は禁止)
 * - `loop === false` の「無音の尾」(素材が鳴り終わったあと)
 * - 分割後の片が 0.5 秒未満になる位置
 */
export function splitAudioCue<C extends AudioCueLike>(
  cue: C,
  t: number,
  opts: SplitAudioCueOptions,
): SplitAudioCueResult<C> {
  const duration = cue.sourceDurationSec;
  if (!isFiniteDuration(duration)) {
    return { ok: false, reason: '素材の尺が取得できないため分割できません' };
  }

  const endSec = effectiveEndSec(cue, opts.totalSec);
  if (t <= cue.startSec + EPS || t >= endSec - EPS) {
    return { ok: false, reason: 'BGM の区間の内側でのみ分割できます' };
  }

  const fadeIn = cue.fadeInSec ?? 0;
  const fadeOut = cue.fadeOutSec ?? 0;
  if (t < cue.startSec + fadeIn - EPS || t > endSec - fadeOut + EPS) {
    return { ok: false, reason: 'フェードの途中では分割できません' };
  }

  if (cue.loop === false) {
    const sourceStart = cue.sourceStartSec ?? 0;
    const soundEndSec = cue.startSec + (duration - sourceStart) / audioCueSpeed(cue);
    if (t >= soundEndSec - EPS) {
      return { ok: false, reason: 'その位置では BGM が鳴っていません' };
    }
  }

  const minSec = minAudioCueSec(opts.kind);
  if (t - cue.startSec < minSec - EPS || endSec - t < minSec - EPS) {
    return {
      ok: false,
      // 最小尺はどちらも 1 秒未満なので、fps があれば「0:フレーム」(切り上げ・秒:フレーム表記) で書く
      reason: opts.fps
        ? `分割すると0:${String(Math.ceil(minSec * opts.fps - EPS)).padStart(2, '0')}未満の区間ができるため分割できません`
        : `分割すると ${minSec} 秒未満の区間ができるため分割できません`,
    };
  }

  const before: C = { ...cue, endSec: t, fadeOutSec: 0 };
  const after: C = {
    ...cue,
    id: opts.newId ?? `${cue.id}-split-${Math.round(t * 1000)}`,
    startSec: t,
    sourceStartSec: posInSource(cue, t - cue.startSec),
    fadeInSec: 0,
  };
  return { ok: true, before, after };
}

/**
 * 同時に鳴るキューをトラックへ割り当てる (§7 の区間彩色)。
 * startSec 順に走査し、終わったトラックを再利用する。返り値のトラック番号は 0 始まりで、
 * 最大値 + 1 = 実際の最大同時再生数 = 書き出しに必要なトラック本数。
 */
export function assignAudioTracks(cues: AudioCueLike[], totalSec?: number): Map<string, number> {
  const ordered = cues
    .map((cue, index) => ({ cue, index }))
    .sort((a, b) => a.cue.startSec - b.cue.startSec || a.index - b.index);

  /** トラックごとの「いま鳴り終わる秒」 */
  const trackEnds: number[] = [];
  const assigned = new Map<string, number>();

  for (const { cue } of ordered) {
    const end = totalSec != null ? effectiveEndSec(cue, totalSec) : cue.endSec ?? Number.POSITIVE_INFINITY;
    let track = trackEnds.findIndex((trackEnd) => trackEnd <= cue.startSec + EPS);
    if (track < 0) {
      track = trackEnds.length;
      trackEnds.push(end);
    } else {
      trackEnds[track] = end;
    }
    assigned.set(cue.id, track);
  }

  return assigned;
}

/** 書き出しに必要なトラック本数 (= 実際の最大同時再生数)。キューが無ければ 0。 */
export function countAudioTracks(cues: AudioCueLike[], totalSec?: number): number {
  const assigned = assignAudioTracks(cues, totalSec);
  let max = -1;
  for (const track of assigned.values()) {
    max = Math.max(max, track);
  }
  return max + 1;
}

/** 旧 `project.bgm` の形 (zod 検証前の素データとして扱う)。 */
export interface LegacyBgmLike {
  materialId?: string;
  isTemporary?: boolean;
  src?: string;
  volume?: number;
  startSec?: number | null;
  endSec?: number | null;
  fadeInSec?: number;
  fadeOutSec?: number;
}

/** 旧 `bgm` から作った 1 キュー (zod 検証前の素データ。`AudioCue` そのものではない)。 */
export interface MigratedBgmCue {
  id: string;
  materialId?: string;
  isTemporary: boolean;
  src?: string;
  startSec: number;
  endSec: number | null;
  volume?: number;
  fadeInSec?: number;
  fadeOutSec?: number;
  /** 旧 BGM は常に区間いっぱいループしていた */
  loop: true;
}

/** 旧 `bgm` を 1 キューへ変換する (§5.3 (a))。素材尺は旧データに無いので付けない。 */
export function legacyBgmToCue(bgm: LegacyBgmLike, id = 'bgm-legacy'): MigratedBgmCue {
  const cue: MigratedBgmCue = {
    id,
    isTemporary: bgm.isTemporary ?? true,
    startSec: bgm.startSec ?? 0,
    endSec: bgm.endSec ?? null,
    loop: true,
  };
  if (bgm.materialId != null) cue.materialId = bgm.materialId;
  if (bgm.src != null) cue.src = bgm.src;
  if (bgm.volume != null) cue.volume = bgm.volume;
  if (bgm.fadeInSec != null) cue.fadeInSec = bgm.fadeInSec;
  if (bgm.fadeOutSec != null) cue.fadeOutSec = bgm.fadeOutSec;
  return cue;
}

/**
 * プロジェクト JSON の移行 (§5.3 (a))。保存済みプロジェクト・`/render` の入力・
 * `pending_proposal` の適用先の入口で通す。以降のコードは `bgmTrack` だけを見る。
 *
 * ```
 * bgmTrack = bgmTrack != null ? bgmTrack       // 新形式があれば空配列でも新形式を優先
 *          : bgm == null      ? []             // どちらも無い = BGM なし
 *          : [1 キューへ変換]
 * ```
 * 旧 `bgm` キーは落とす (保存時に書かないため)。提案の正規化 (§5.3 (b)) とは別物で、
 * 「変更なし」と「全削除」の三値はここでは扱わない。
 */
export function migrateProjectBgmTrack(raw: unknown): unknown {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }
  const project = raw as Record<string, unknown>;
  if (!('bgm' in project) && project.bgmTrack != null) {
    return project;
  }

  const { bgm, ...rest } = project;
  if (project.bgmTrack != null) {
    return rest;
  }
  if (bgm == null || typeof bgm !== 'object' || Array.isArray(bgm)) {
    return { ...rest, bgmTrack: [] };
  }
  return { ...rest, bgmTrack: [legacyBgmToCue(bgm as LegacyBgmLike)] };
}

/**
 * SE キューを既定値どおりに組み立てる (§4.3-D)。BE の `audio_cue.build_se_cue` と同じ形。
 *
 * 既定は「`endSec` 省略 = 素材の実尺どおり 1 回だけ鳴る・音量 1.0・フェード無し・`loop: false`」。
 * `loop` を省略すると BGM の既定 (周回) になり短い効果音が繰り返されるので、必ず false を明示する。
 * `sourceDurationSec` は**必須** (無いと区間の終わりが決まらず「動画の残り全部」鳴ってしまい、
 * タイムラインのマーカーも書き出しのクリップ長も全幅になる)。渡されなければ例外にする。
 */
export function createSeCue(params: {
  id: string;
  materialId: string;
  isTemporary?: boolean;
  startSec: number;
  /** 素材の実尺 (秒)。**必須** — 無いと区間の終わりが決まらず動画の残り全部鳴ってしまう */
  sourceDurationSec: number;
  volume?: number;
  endSec?: number | null;
}): {
  id: string;
  materialId: string;
  isTemporary: boolean;
  startSec: number;
  volume: number;
  loop: false;
  sourceDurationSec: number;
  endSec?: number | null;
} {
  if (!(params.sourceDurationSec > 0) || !Number.isFinite(params.sourceDurationSec)) {
    throw new Error(MISSING_SE_SOURCE_DURATION_MESSAGE);
  }
  return {
    id: params.id,
    materialId: params.materialId,
    isTemporary: params.isTemporary ?? true,
    startSec: Math.max(0, params.startSec),
    volume: params.volume ?? DEFAULT_SE_VOLUME,
    loop: false,
    sourceDurationSec: params.sourceDurationSec,
    ...(params.endSec != null ? { endSec: params.endSec } : {}),
  };
}
